import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import useReveal from '../hooks/useReveal';
import CountUp from '../components/CountUp';
import TokenRevealText from '../components/TokenRevealText';

const Home = () => {
  useReveal();
  const stats = [
    { value: '20+', label: 'Years' },
    { value: '40+', label: 'Engineers' },
    { value: '20k sq.ft', label: 'Facility' },
    { value: '100+', label: 'Projects' },
  ];

  const capabilities = [
    {
      title: 'Product Engineering',
      sub: 'Concept to Production',
      link: '/services#product-engineering',
    },
    {
      title: 'Mechanical Design',
      sub: 'CAD and DFM',
      link: '/services#mechanical-design',
    },
    {
      title: 'Tooling & Mold',
      sub: 'Precision Tooling',
      link: '/services#tooling-mold',
    },
    {
      title: 'Manufacturing',
      sub: 'Production at Scale',
      link: '/services#manufacturing',
    },
  ];

  const clients = [
    { name: 'Analogics', logo: '/clients/analogics.png', initial: 'A' },
    { name: 'JARSH Safety', logo: '/clients/jarsh-safety.png', initial: 'JS' },
    { name: 'Artlab India Pvt Ltd', logo: '/clients/artlab-india.png', initial: 'AI' },
    { name: 'Panasonic', logo: '/clients/panasonic.png', initial: 'P' },
    { name: 'BDL', logo: '/clients/bdl.png', initial: 'BDL' },
    { name: 'ECIL', logo: '/clients/ecil.png', initial: 'ECIL' },
    { name: 'EPSON', logo: '/clients/epson.png', initial: 'E' },
    { name: 'Apex Global', logo: '/clients/apex-global.png', initial: 'AG' },
    { name: 'Feedback Infra', logo: '/clients/feedback-infra.png', initial: 'FI' },
    { name: 'IndusInd Bank', logo: '/clients/indusind-bank.png', initial: 'IB' },
  ];

  return (
    <main className="home page-enter">

      {/* ===== HERO ===== */}
      <section className="hero hero-split">
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="hero-heading">
              <TokenRevealText
                parts={[
                  { text: 'Precision Engineering & Manufacturing' },
                  { text: 'From Concept to Production', className: 'hero-accent', lineBreakBefore: true },
                ]}
                speed="medium"
              />
            </h1>
            <p className="hero-sub">
              <TokenRevealText
                text="End-to-end engineering and manufacturing solutions. We deliver integrated mechanical solutions tailored to real-world applications – from enclosures and thermal systems to connectivity and precision tooling. Designed, engineered, and manufactured for performance, reliability, and scale."
                speed="medium"
              />
            </p>
          </div>
        </div>
      </section>

      <section className="hero-media-row">
        <div className="container">
          <div className="hero-media hero-media-row-inner">
            <div className="hero-media-split">
              <img
                src={`${process.env.PUBLIC_URL}/images/AR7A3721.JPG`}
                alt="PCB assembly"
              />
              <img
                src={`${process.env.PUBLIC_URL}/images/C2P-4.jpg`}
                alt="Mechanical enclosure"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section className="trust-strip">
        <div className="container trust-strip-inner">
          {stats.map((s, i) => (
            <div key={s.label} className={`trust-item reveal reveal-scale reveal-d${i + 1}`}>
              <span className="trust-value"><CountUp value={s.value} /></span>
              <span className="trust-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== COMPANY OVERVIEW ===== */}

      {/* ===== CAPABILITIES ===== */}
      <section className="section capabilities-section">
        <div className="container">
          <div className="section-head-center reveal">
            <h2><TokenRevealText text="Capabilities" speed="medium" /></h2>
            <div className="rule" />
          </div>
          <div className="cap-grid">
            {capabilities.map((c, i) => (
              <Link to={c.link} key={c.title} className={`cap-box reveal reveal-scale reveal-d${i + 1}`}>
                <h4><TokenRevealText text={c.title} speed="medium" /></h4>
                <p><TokenRevealText text={c.sub} speed="medium" /></p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ANALOGICS + C2P SIGNATURE ===== */}
      <section className="section signature-section">
        <div className="container signature-layout">
          <div className="signature-text reveal reveal-left">
            <p className="eyebrow-red"><TokenRevealText text="Analogics + C2P" speed="medium" /></p>
            <h2><TokenRevealText text="Integrated Product Development" speed="medium" /></h2>
            <p className="body-text">
              <TokenRevealText
                text="Electronics, firmware, and hardware development aligned with mechanical engineering and manufacturing to deliver complete products."
                speed="medium"
              />
            </p>
            <Link to="/services#analogics-c2p" className="btn-navy signature-cta">Explore Analogics + C2P</Link>
          </div>
          <div className="signature-flow-card reveal reveal-right">
            <div className="signature-flow">
              <div className="flow-row flow-row-primary">
                <div className="flow-node flow-node-neutral">Product Requirement</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-neutral">Concept & System Design</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-neutral">Parallel Development</div>
              </div>
              <div className="flow-branch">
                <span className="flow-branch-up">↗</span>
                <span className="flow-branch-down">↘</span>
              </div>
              <div className="flow-row flow-row-top">
                <div className="flow-node flow-node-analogics">Electronics · Analogics</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-analogics">PCB / Firmware / Software</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-neutral">Integration</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-neutral">Validation</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-neutral">Production</div>
              </div>
              <div className="flow-row flow-row-bottom">
                <div className="flow-node flow-node-c2p">Mechanical · C2P</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-c2p">Enclosure / Tooling / Prototype</div>
                <span className="flow-arrow">→</span>
                <div className="flow-node flow-node-neutral">Integration</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FACILITY DARK SECTION ===== */}
      <section className="section facility-section">
        <div className="container facility-layout">
          <div className="facility-image reveal reveal-left">
            <img
              src={`${process.env.PUBLIC_URL}/images/AR7A3925.jpg`}
              alt="Advanced manufacturing"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = `${process.env.PUBLIC_URL}/images/C2P2.jpg`;
              }}
            />
          </div>
          <div className="facility-text reveal reveal-right">
            <p className="eyebrow-red"><TokenRevealText text="Facility" speed="medium" /></p>
            <h2><TokenRevealText text="Advanced Manufacturing Facility" speed="medium" /></h2>
            <ul className="facility-list">
              <li><TokenRevealText text="20,000+ sq. ft. facility" speed="medium" /></li>
              <li><TokenRevealText text="High-Precision Tooling" speed="medium" /></li>
              <li><TokenRevealText text="Modern Injection Molding Machines" speed="medium" /></li>
            </ul>
            <Link to="/infrastructure" className="btn-navy">View Infrastructure</Link>
          </div>
        </div>
      </section>

      {/* ===== CLIENTS BAR ===== */}
      <section className="clients-bar">
        <div className="container">
          <div className="section-head-center reveal">
            <p className="eyebrow-blue"><TokenRevealText text="Our Clients" speed="medium" /></p>
            <h2><TokenRevealText text="Trusted by Leading Companies" speed="medium" /></h2>
            <div className="rule" />
          </div>
          <div className="clients-logos">
            {clients.map((client, i) => (
              <div key={client.name} className="client-logo-box">
                <img
                  src={`${process.env.PUBLIC_URL}${client.logo}`}
                  alt={`${client.name} logo`}
                  className="client-logo-img"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement?.classList.add('logo-fallback');
                  }}
                />
                <span className="client-logo-fallback">{client.initial}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA DARK ===== */}
      <section className="final-cta">
        <div className="container final-cta-inner">
          <div>
            <h2><TokenRevealText text="Ready to Build Your Product?" speed="medium" /></h2>
          </div>
          <Link to="/contact" className="btn-red">Let's Connect</Link>
        </div>
      </section>

    </main>
  );
};

export default Home;
